import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AwsRegions } from 'components/regions';
import { BackButton } from 'components/buttons';
import Header from 'components/header/Header';
import AutocompleteTooltip from 'components/Inputs/InputsWithTooltip/AutocompleteTooltip';
import useCreateEnvironment from './hooks/useCreateEnvironment';
import { NewEnvironmentInput } from 'types/Environment';
import { NButton, NInput } from '@innersource-nn/novoui-react';
import NInputWithChips from 'components/Inputs/Labels Input/NInputWithChips';
import NCheckboxTooltip from 'components/Inputs/InputsWithTooltip/NCheckboxTooltip';
import SimpleLoader from 'components/loader/SimpleLoader';
import NTextAreaQuill from 'components/Inputs/TextAreaQuill/NTextAreaQuill';
import useOrganizationListDropdown from './hooks/useOrganizationListDropdown';

// Adding the region ID also to the label so bith are displayed like: us-east-2:US East (Ohio)
const awsRegionsCombined = () => {
    return AwsRegions.map((item) => {
        return { label: `${item.name}: ${item.label}`, name: item.name };
    });
};

const EnvironmentForm = () => {
    const params = useParams();

    const defaultValues = {
        label: '',
        organizationUri: params.uri ?? '',
        SamlGroupName: [],
        SamlFullDevGroupName: [],
        SamlBasicDevGroupName: [],
        SamlReaderGroupName: [],
        tags: [],
        description: '',
        AwsProdAccountId: '',
        AwsDevAccountId: '',
        AwsTestAccountId: '',
        AwsToolAccountId: '',
        AwsValAccountId: '',
        region: '',
        blockpreview: 'All',
        quicksight_enabled: false,
        defaultPipelineVpcId: '',
        identityProviderName: '',
    };
    const [formData, setFormData] =
        useState<NewEnvironmentInput>(defaultValues);
    const [selectedRegionName, setSelectedRegionName] = useState<string>(
        defaultValues.region,
    );
    const [selectedOrganizationUri, setSelectedOrganizationUri] =
        useState<string>(defaultValues.organizationUri);

    const { handleCreate, createLoading } = useCreateEnvironment();
    const { options, loading } = useOrganizationListDropdown();

    const updateLabels = (inputName: string, newValue: string[]) => {
        setFormData((prevData: any) => {
            return {
                ...prevData,
                [inputName]: newValue,
            };
        });
    };
    const isButtonDisabled = () => {
        const {
            label,
            description,
            region,
            AwsProdAccountId,
            organizationUri,
        } = formData;
        return (
            label === '' ||
            description === '' ||
            organizationUri === '' ||
            AwsProdAccountId === '' ||
            region === ''
        );
    };

    return (
        <div>
            <BackButton />
            <Header title="Link Environment" />
            {createLoading || loading ? (
                <SimpleLoader />
            ) : (
                <div className="flex flex-col gap-16 info-container">
                    <AutocompleteTooltip
                        label="Organization *"
                        key="organizationUri"
                        placeholder="Search for an organization..."
                        value={selectedOrganizationUri}
                        items={options}
                        handleSelected={(selectedOption) => {
                            setFormData({
                                ...formData,
                                organizationUri: selectedOption.value ?? '',
                            });
                            setSelectedOrganizationUri(selectedOption.label);
                        }}
                        disabled={!!params.uri}
                    />
                    <NInput
                        name="label"
                        className="bg-white"
                        placeholder="Environment name..."
                        label="Environment Name *"
                        variant="outline"
                        modelValue={formData.label}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                label: e.target.value,
                            })
                        }
                    />
                    <NTextAreaQuill
                        label="Environment Description *"
                        placeholder="Environment description..."
                        variant="outline"
                        modelValue={formData.description}
                        onChange={(value) =>
                            setFormData({
                                ...formData,
                                description: value,
                            })
                        }
                    />
                    <div className="grid xl:grid-cols-4 lg:grid-cols-3 grid-cols-2 gap-16">
                        <AutocompleteTooltip
                            label="Select AWS Region *"
                            placeholder="Search for AWS region...."
                            key="environment-region-select"
                            value={selectedRegionName}
                            items={awsRegionsCombined()}
                            handleSelected={(selectedOption) => {
                                setFormData({
                                    ...formData,
                                    region: selectedOption.name ?? '',
                                });
                                setSelectedRegionName(selectedOption.label);
                            }}
                        />
                        <NInput
                            label="Production AWS Account *"
                            variant="outline"
                            placeholder="Production AWS Account..."
                            modelValue={formData.AwsProdAccountId}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    AwsProdAccountId: e.target.value,
                                })
                            }
                        />
                        <NInput
                            label="Tooling AWS Account"
                            variant="outline"
                            placeholder="Tooling AWS Account..."
                            modelValue={formData.AwsToolAccountId}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    AwsToolAccountId: e.target.value,
                                })
                            }
                        />
                        <NInput
                            label="Development AWS Account"
                            variant="outline"
                            placeholder="Development AWS Account..."
                            modelValue={formData.AwsDevAccountId}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    AwsDevAccountId: e.target.value,
                                })
                            }
                        />

                        <NInput
                            label="Test AWS Account"
                            variant="outline"
                            placeholder="Testing AWS Account..."
                            modelValue={formData.AwsTestAccountId}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    AwsTestAccountId: e.target.value,
                                })
                            }
                        />
                        <NInput
                            label="Validation AWS Account"
                            variant="outline"
                            placeholder="Validation AWS Account..."
                            modelValue={formData.AwsValAccountId}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    AwsValAccountId: e.target.value,
                                })
                            }
                        />
                        <NInputWithChips
                            label="Admin Groups"
                            name="SamlGroupName"
                            labels={formData.SamlGroupName}
                            content="Add descriptive tags or keywords to categorize your datasets using meaningful words."
                            placeholder="Type and press enter... "
                            modelValue=""
                            updateLabels={updateLabels}
                        />
                        <NInputWithChips
                            label="Developer Groups"
                            name="SamlFullDevGroupName"
                            labels={formData.SamlFullDevGroupName}
                            content="Add descriptive tags or keywords to categorize your datasets using meaningful words."
                            placeholder="Type and press enter... "
                            modelValue=""
                            updateLabels={updateLabels}
                        />
                        <NInputWithChips
                            label="Reader Groups"
                            name="SamlReaderGroupName"
                            labels={formData.SamlReaderGroupName}
                            content="Add descriptive tags or keywords to categorize your datasets using meaningful words."
                            placeholder="Type and press enter... "
                            modelValue=""
                            updateLabels={updateLabels}
                        />
                        <NInput
                            label="Identity Provider Name"
                            variant="outline"
                            placeholder="Identity Provider Name..."
                            modelValue={formData.identityProviderName}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    identityProviderName: e.target.value,
                                })
                            }
                        />
                        <NInput
                            label="Default VPC Id"
                            variant="outline"
                            placeholder="Default VPC Id..."
                            modelValue={formData.defaultPipelineVpcId}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    defaultPipelineVpcId: e.target.value,
                                })
                            }
                        />
                        <NInputWithChips
                            label="Tags"
                            name="tags"
                            content="Add descriptive tags or keywords to categorize your datasets using meaningful words."
                            placeholder="Type and press enter... "
                            updateLabels={updateLabels}
                            labels={formData.tags}
                            modelValue=""
                        />
                        <NCheckboxTooltip
                            label="Block preview to all datasets"
                            content="Allows users to glimpse into their environments datasets before requesting for full access to that dataset."
                            name="blockpreview"
                            checkboxSize="medium"
                            checked={formData.blockpreview === 'All'}
                            onChange={() =>
                                setFormData({
                                    ...formData,
                                    blockpreview:
                                        formData.blockpreview === 'All'
                                            ? 'None'
                                            : 'All',
                                })
                            }
                        />
                        <NCheckboxTooltip
                            content="Can be enabled to allow Amazon Quicksight to gain access to the tables in your environment."
                            label="Enable quicksight access"
                            name="quicksight_enabled"
                            checkboxSize="medium"
                            checked={formData.quicksight_enabled}
                            onChange={() =>
                                setFormData({
                                    ...formData,
                                    quicksight_enabled:
                                        !formData.quicksight_enabled,
                                })
                            }
                        />
                    </div>
                    <div className="flex justify-end">
                        <NButton
                            variant="fill"
                            square
                            onClick={() => handleCreate(formData)}
                            disabled={isButtonDisabled()}
                        >
                            Create Environment
                        </NButton>
                    </div>
                </div>
            )}
        </div>
    );
};
export default EnvironmentForm;
